/* global dataLayer */
/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
/* eslint no-underscore-dangle: [2, { "allow": ["foo_", "_bar"] }]*/
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import Observer from 'react-intersection-observer';
import NoSSR from 'react-no-ssr';
import {
  trans,
  withClientConfig
} from '@spotahome/soyuz/client';
import Metrics from '@spotahome/soyuz-metrics';
import MobileDetect from 'mobile-detect';
import {
  FloatingButton,
  Button,
  ListIcon,
  MapIcon
} from '@spotahome/ui-library';
import '@spotahome/ui-library/lib/components/InstantBookingBanner.css';
import '@spotahome/ui-library/lib/components/FloatingButton/FloatingButton.css';

import mapPointShape from '@search/shapes/mapPoint';
import poiShape from '@search/shapes/poi';
import {
  adaptFilters,
  getDefaultFilters,
  adaptFiltersToUrl
} from '@search/utils/filters';

import { getMarkersQuery } from '@common/graphql/queries/markers';
import { getCancellationPoliciesQuery } from '@common/graphql/queries/cancellationPolicy';
import getGqlClient from '@common/graphql/client';

import {
  getMarkersRequest,
  getUnitPricesRequest
} from '@common/api/marketplace-client';

import { setPoiRequest, deletePoiRequest } from '@common/api/poi-client';

import debounce from '@common/utils/debounce';
import { coordsInGivenBounds } from '@common/utils/google-maps';

import { InstanceManager, CustomDimensionsManager } from '@common/tracking';

import getInternalLinkingContainer from '@common/components/internalLinking';
import HomeCardList from '@common/components/HomeCardList';
import HomecardMap from '@common/components/HomecardMap';
import TotalResults from '@search/components/TotalResults';
import Paginator from '@search/components/Paginator';
import SEOContent from '@search/components/SEOContent';
import SearchBanner from '@search/components/SearchBanner';

import CityDiscountsToast from '@search/components/CityDiscountsToast';

import RefreshIcon from '@common/components/icons/Refresh';

import { shiftMarkers } from '../../../../pages/search/utils/markerShifter';

import IconLoading from '../../ui/icon/icon-loading';
import AdUrlService from '../../cornucopia/ads/ad-url-service';

import CardPaginator from '../lib/card-paginator';

import { removeSeoFilters } from '../../../../pages/search/utils/seo-mode';
import { trackSearchQueryRequest } from '@common/api/search-tracker-client';

import ContextBar from './ContextBar'; // TODO: requires window

import Filters from './Filters';
import FiltersHeader from './FiltersHeader';

import Map from './Map';

// If a POI is defined, the list of markers will be retrieved ordered by distance
// and the frontend will order by relevance each set defined by this const.
const MAP_ID = 'themap';
const PLACE_SELECTED_EVENT_CATEGORY = 'interactive-map-add-reference-point';

const VIEW_MAP_LIST_EVENT_CATEGORY = 'Search';
const VIEW_MAP_LIST_EVENT_ACTION = 'View map-list toggle';
const VIEW_MAP_LIST_EVENT_VIEW_MAP_TAG = 'View map';
const VIEW_MAP_LIST_EVENT_VIEW_LIST_TAG = 'View list';

const INITIAL_STATE = {
  maxTotalResults: 0,
  adIdCardMouseEnter: null,
  adIdCardMouseLeave: null,
  adMarkerClicked: null,
  adMarkerMouseEnter: null,
  adMarkerMouseLeave: null,
  isFiltersHidden: true,
  unitPrices: [],
  isMenuOpen: false,
  canUpdateBoundaries: false
};

const isMobileScreen = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(max-width: 700px)').matches;

const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  new MobileDetect(window.navigator.userAgent).phone();

const InternalLinking = getInternalLinkingContainer('SearchPage');

const serializeGaParameters = (areaId, serializeArguments) =>
  [
    areaId,
    serializeArguments['move-in'],
    serializeArguments['move-out'],
    serializeArguments.budget,
    serializeArguments['type[]'].join(),
    serializeArguments['topology[]'].join(),
    serializeArguments['bathrooms[]'].join(),
    serializeArguments.bed,
    serializeArguments['features[]'].join(),
    serializeArguments.poi,
    serializeArguments.totalHomecards,
    serializeArguments.totalHomecardsWithIB,
    serializeArguments.verified,
    serializeArguments.superLister,
    '',
    '',
    serializeArguments.instantBooking,
    serializeArguments['areaId[]'].join(),
    serializeArguments.flexibleMoveIn,
    serializeArguments.flexibleMoveOut
  ].join(';');

const sendCityDateEvent = (areaId, parameters) => {
  const gaParameters = serializeGaParameters(areaId, parameters);
  Metrics.ga.sendEventWithClientId('dwh-city-date', gaParameters, {
    nonInteraction: true
  });
};

const isPoiFilled = poi => poi && poi.lat && poi.lng;

const sendPageView = (cityId, filters) => {
  const url = AdUrlService.getSearchUrlFromFilters(cityId, filters);

  Metrics.ga.set('page', url);
  Metrics.ga.sendPageView();
};

const isPointOfInterestInBounds = (pointOfInterest, limits) => {
  if (!pointOfInterest) {
    return false;
  }

  const boundsNeSw = {
    ne: {
      lat: limits.ne.lat,
      lng: limits.ne.lng
    },
    sw: {
      lat: limits.sw.lat,
      lng: limits.sw.lng
    }
  };

  return coordsInGivenBounds(pointOfInterest, boundsNeSw);
};

const pushDataLayerProductList = (nextMarkers, moveIn, moveOut) => {
  if (typeof dataLayer !== 'undefined') {
    const productIdList = nextMarkers.slice(0, 3).map(ru => ru.id);

    dataLayer.push({
      PageType: 'Listingpage',
      HashedEmail: '',
      ProductIDList: productIdList,
      din: moveIn,
      dout: moveOut
    });
  }
};

const parseFlexibleMoveFilters = ({
  flexibleMoveIn,
  flexibleMoveOut,
  ...other
}) => ({
  ...other,
  flexibleMoveIn: flexibleMoveIn === 0 ? '0' : false,
  flexibleMoveOut: flexibleMoveOut === 0 ? '0' : false
});

class Search extends PureComponent {
  constructor(props) {
    super(props);

    const {
      initialFiltersFromQuery,
      seoMode,
      sessionPoi,
      initialMarkerList,
      numberOfMarkers,
      initialUnitPrices,
      itemsPerPage
    } = props;

    const filters = {
      ...getDefaultFilters(),
      ...initialFiltersFromQuery,
      seoMode
    };

    let pointOfInterest = null;

    if (filters.sortBy === 'POI' && filters.poiLat && filters.poiLng) {
      pointOfInterest = {
        lat: filters.poiLat,
        lng: filters.poiLng,
        name: filters.poiName || '',
        placeId: filters.poiId || ''
      };
      if (
        isPoiFilled(sessionPoi) &&
        pointOfInterest.lat === sessionPoi.lat &&
        pointOfInterest.lng === sessionPoi.lng
      ) {
        pointOfInterest = sessionPoi;
      }
    } else if (isPoiFilled(sessionPoi)) {
      pointOfInterest = sessionPoi;
    }

    this.state = {
      ...INITIAL_STATE,
      markerList: initialMarkerList,
      numberOfMarkers,
      isMapVisible: !isMobileScreen(),
      filters,
      pointOfInterest,
      unitPrices: initialUnitPrices,
      isMapActive: false
    };

    this.cardPaginator = new CardPaginator({ pageSize: itemsPerPage });

    this.cardPaginator.initializeData(this.state.markerList);
  }

  componentDidMount() {
    const { filters, pointOfInterest, markerList } = this.state;

    this.cdManager = CustomDimensionsManager(
      InstanceManager.getClientSideInstance()
    );

    trackSearchQueryRequest(
      this.props.city.id,
      adaptFilters(filters),
      this.state.numberOfMarkers
    ).catch (err => {
      console.error(err);
    });

    if (isPoiFilled(pointOfInterest)) {
      this.updatePointOfInterestState(pointOfInterest);
    }

    if (markerList.length > 0) {
      pushDataLayerProductList(
        markerList,
        filters['move-in'],
        filters['move-out']
      );
    }

    window.addEventListener(
      'resize',
      debounce(() => this.setState({ isMapVisible: !isMobileScreen() }), 250)
    );

    this.requestAllMarkers();

    if (filters['move-in']) {
      this.getCancellationPolicies(filters['move-in'], this.props.city.id);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const prevFilters = prevState.filters;

    if (JSON.stringify(this.state.filters) !== JSON.stringify(prevFilters)) {
      const cityId = this.props.city.id;
      const initialHomecards = Object.values(prevProps.initialHomecards);
      const totalHomecards = initialHomecards.length;

      const totalHomecardsWithIB = initialHomecards.filter(
        homecard => homecard.isInstantBooking
      ).length;

      const serializeArguments = {
        ...this.state.filters,
        totalHomecards,
        totalHomecardsWithIB
      };

      sendCityDateEvent(cityId, serializeArguments);

      this.requestNewContent(prevFilters);

      const checkInDate = this.state.filters['move-in'];
      if (checkInDate && checkInDate !== prevFilters['move-in']) {
        this.getCancellationPolicies(checkInDate, cityId);
      }
    }
  }

  getCancellationPolicies = async (checkInDate, cityId) => {
    try {
      const { data: cancellationPolicies } = await getCancellationPoliciesQuery(
        getGqlClient()
      )(checkInDate, cityId);
      this.setState({ ...cancellationPolicies });
    } catch {
      this.setState({ cancellationPolicies: {} });
    }
  };

  handlePlaceSelected = async place => {
    const { name, lat, lng } = place;

    if (name && lat && lng) {
      Metrics.ga.sendEvent(
        PLACE_SELECTED_EVENT_CATEGORY,
        name,
        `${lat},${lng}`
      );
      Metrics.ga.sendEvent('Search', 'sort-by', 'distance');
    }

    // TODO: check if it works
    try {
      const city = this.props.city.id;
      await setPoiRequest(city, place);
    } catch (error) {
      console.error(error);
    }

    this.updatePointOfInterestState(place);
  };

  handlePlaceUnselected = async () => {
    try {
      const city = this.props.city.id;
      await deletePoiRequest(city);
    } catch (error) {
      console.error(error);
    }

    this.updatePointOfInterestState(null);
  };

  handleCardMouseEnter = adId => {
    this.setState({
      adMarkerClicked: null,
      adIdCardMouseEnter: adId,
      adIdCardMouseLeave: null
    });
  };

  handleCardMouseLeave = adId => {
    this.setState({
      adMarkerClicked: null,
      adIdCardMouseEnter: null,
      adIdCardMouseLeave: adId
    });
  };

  handleMarkerClicked = adIdCount => {
    if (
      this.state.adMarkerClicked &&
      adIdCount &&
      adIdCount.id === this.state.adMarkerClicked.id
    ) {
      this.setState({
        adMarkerClicked: null,
        isFiltersHidden: true
      });
    } else {
      this.setState({
        adMarkerClicked: adIdCount,
        isFiltersHidden: true
      });
    }
  };

  handleMarkerMouseEnter = adIdCount => {
    this.setState({
      adMarkerMouseEnter: adIdCount,
      adMarkerMouseLeave: null
    });
  };

  handleMarkerMouseLeave = adIdCount => {
    this.setState({
      adMarkerMouseLeave: adIdCount,
      adMarkerMouseEnter: null
    });
  };

  handleNextButtonClick = () => {
    if (this.cardPaginator.getNextPageNumber()) {
      this.cardPaginator.nextPage();
      this.onNewPageContent();
    }
  };

  handlePreviousButtonClick = () => {
    if (this.cardPaginator.getPreviousPageNumber()) {
      this.cardPaginator.previousPage();
      this.onNewPageContent();
    }
  };

  handleMapWillUnmount = () => {
    this.cardPaginator.restoreOriginalData();
    this.onNewPageContent();
  };

  handleMapDidMount = map => {
    this.boundariesFilter = elem =>
      map.isMarkerInBoundaries({
        lat: elem.coord[1],
        lng: elem.coord[0]
      });
  };

  onNewPageContent = () => {
    const currentPageMarkers = this.cardPaginator.getCurrentPageContent();

    this.setState(
      prevState => ({
        markerList: currentPageMarkers,
        adMarkerClicked: null,
        adIdCardMouseEnter: null,
        adIdCardMouseLeave: null,
        filters: {
          ...prevState.filters,
          page: this.cardPaginator.getCurrentPageNumber()
        },
        newMapBoundaries: false
      }),
      () => {
        this.updateURL(this.state.filters);
      }
    );
  };

  getMarkers = async queryStr => {
    let newMarkers = [];

    try {
      const { data } = await getMarkersRequest(this.props.city.id, {
        queryStr
      });

      newMarkers = data.ok ? data.data : [];
    } catch (error) {
      if (error.message !== 'CANCELED') {
        console.error(error);
      }
    }

    this.updateMarkersState(newMarkers);

    return {
      numberResults: newMarkers.length
    };
  };

  getUnitPrices = async queryStr => {
    try {
      const { data } = await getUnitPricesRequest(this.props.city.id, {
        queryStr
      });

      if (data.ok) {
        this.setState({
          unitPrices: data.unitPrices.filter(
            price => price <= this.props.maxPriceInCity
          )
        });
      }
    } catch (error) {
      if (error.message !== 'CANCELED') {
        console.error(error);
      }
    }
  };

  getCurrentPage = () => this.cardPaginator.getCurrentPageNumber();

  sendViewMapListToggleEvent = isMapActive => {
    Metrics.ga.sendEvent(
      VIEW_MAP_LIST_EVENT_CATEGORY,
      VIEW_MAP_LIST_EVENT_ACTION,
      isMapActive
        ? VIEW_MAP_LIST_EVENT_VIEW_MAP_TAG
        : VIEW_MAP_LIST_EVENT_VIEW_LIST_TAG
    );
  };

  handleToggleActiveView = () => {
    this.setState(({ isMapActive }) => {
      const toggled = !isMapActive;

      this.sendViewMapListToggleEvent(toggled);

      return { isMapActive: toggled };
    });
  };

  onFiltersToggleClick = () => {
    this.setState(({ isFiltersHidden }) => ({
      isFiltersHidden: !isFiltersHidden
    }));
  };

  updatePointOfInterestState = point => {
    const inBounds = isPointOfInterestInBounds(point, this.props.city.limits);

    let pointOfInterest = null;
    let sortBy;
    let poi = {
      poiLat: '',
      poiLng: '',
      poiName: '',
      poiId: '',
      sortBy
    };

    if (inBounds) {
      pointOfInterest = point;
      sortBy = 'POI';
      poi = {
        poiLat: point.lat,
        poiLng: point.lng,
        poiName: point.name,
        poiId: point.placeId,
        sortBy
      };
    }

    this.updateFiltersState(poi);
    this.setState({ pointOfInterest, sortBy });
  };

  updateMarkersState = nextMarkers => {
    const numberOfMarkers = nextMarkers.length;
    const shiftedMarkers = shiftMarkers(nextMarkers);

    this.cardPaginator.initializeData(shiftedMarkers);
    this.cardPaginator.filter(this.boundariesFilter);

    this.setState(
      ({ maxTotalResults }) => ({
        nextMarkers: shiftedMarkers,
        maxTotalResults: Math.max(maxTotalResults, numberOfMarkers),
        numberOfMarkers
      }),
      this.onNewPageContent
    );
  };

  updateSortBy = async sortBy => {
    if (this.state.sortBy === 'POI') {
      await this.handlePlaceUnselected();
    }
    this.updateFiltersState({ sortBy });
  };

  toggleMenu = () =>
    this.setState(({ isMenuOpen }) => ({ isMenuOpen: !isMenuOpen }));

  updateURL = filters => {
    AdUrlService.setSearchLocationUrl(
      this.props.city.id,
      parseFlexibleMoveFilters(filters)
    );
  };

  isInstantBooking = () => {
    const initialHomecards = Object.values(this.props.initialHomecards);
    return initialHomecards.some(homecard => homecard.isInstantBooking);
  };

  handleRefreshMapBoundaries = () => {
    this.setState({ newMapBoundaries: false });
    this.cardPaginator.filter(this.boundariesFilter);
    this.onNewPageContent();
  };

  handleMapNewBounds = () => {
    this.setState({ newMapBoundaries: true });
    if (this.state.canUpdateBoundaries && !isMobileScreen()) {
      this.handleRefreshMapBoundaries();
    }
  };

  calculateMarkersWithoutBounds = () => {
    this[MAP_ID].setInitializePosition(true);
    this.cardPaginator.restoreOriginalData();
    this.onNewPageContent();
  };

  isPrevButtonDisabled = () =>
    this.cardPaginator.getPreviousPageNumber() === null;

  isNextButtonDisabled = () => this.cardPaginator.getNextPageNumber() === null;

  requestAllMarkers = async () => {
    try {
      const { filters } = this.state;

      const { data } = await getMarkersQuery(getGqlClient())(
        this.props.city.id,
        adaptFilters(filters, this.props.sortingAlgorithm)
      );
      const markers = shiftMarkers(data.search.markers);

      this.cardPaginator.initializeData(markers);
      this.cardPaginator.setCurrentPage(parseInt(filters.page, 10));
      this.setState({
        numberOfMarkers: markers.length,
        canUpdateBoundaries: true,
        hasInstantBooking: markers.some(marker => marker.instantBooking)
      });
    } catch (error) {
      console.error(error);
    }
  };

  requestNewContent = debounce(async prevFilters => {
    const currentFilters = adaptFiltersToUrl(
      this.state.filters,
      this.props.sortingAlgorithm
    );
    const prevFiltersAdapted = adaptFiltersToUrl(
      prevFilters,
      this.props.sortingAlgorithm
    );
    const urlQueryString = AdUrlService.createQueryString(currentFilters);

    if (this.shouldUpdatePrices(prevFiltersAdapted)) {
      this.getUnitPrices(urlQueryString);
    }

    let numberResults = this.state.numberOfMarkers;
    if (this.shouldUpdateMarkers(prevFiltersAdapted)) {
      numberResults = (await this.getMarkers(urlQueryString)).numberResults;
    }

    this.cdManager.setSearchQueryCustomDimensions(
      this.props.city.id,
      adaptFilters(this.state.filters),
      numberResults
    );

    trackSearchQueryRequest(
      this.props.city.id,
      adaptFilters(this.state.filters),
      numberResults
    ).catch (err => {
      console.error(err);
    });

    sendPageView(this.props.city.id, currentFilters);
  }, 250);

  shouldUpdateMarkers = prevFilters => {
    const currentFilters = this.state.filters;
    const filterKeys = Object.keys(currentFilters).filter(
      key => key !== 'page'
    );

    return filterKeys.some(key => currentFilters[key] !== prevFilters[key]);
  };

  shouldUpdatePrices = prevFilters => {
    const currentFilters = this.state.filters;
    const filterKeys = Object.keys(currentFilters).filter(
      key => key !== 'page' && key !== 'sortBy'
    );

    return filterKeys.some(key => currentFilters[key] !== prevFilters[key]);
  };

  updateFiltersState = filtersToBeMerged => {
    const updatedFilters = Object.assign(
      {},
      this.state.filters,
      filtersToBeMerged
    );

    const newState = Object.assign({}, this.state, {
      filters: removeSeoFilters(updatedFilters)
    });

    // remove rooms when user deselect apartments
    if (
      filtersToBeMerged['type[]'] &&
      !filtersToBeMerged['type[]'].includes('apartments')
    ) {
      newState.filters['topology[]'] = [];
    }

    this.setState(newState);
  };

  clearFilters = () => {
    if (!isMobileDevice()) {
      this[MAP_ID].setInitializePosition(true);
    }
    this.updateFiltersState(getDefaultFilters());
  };

  renderFullFilters = () =>
    !this.state.isFiltersHidden && (
      <Fragment>
        <div className="search-filters-full">
          <Filters
            currency={this.props.currency}
            filters={this.state.filters}
            hasInstantBooking={this.state.hasInstantBooking}
            maxPriceInCity={this.props.maxPriceInCity}
            simpleFilters={false}
            unitPrices={this.state.unitPrices}
            updateFiltersWith={this.updateFiltersState}
            cityName={this.props.city.id}
            areas={this.props.areas}
          />
        </div>
        <FiltersHeader
          results={this.state.numberOfMarkers}
          citySlug={this.props.city.id}
          totalResults={this.cardPaginator.getOriginalLength()}
          handleFiltersToggle={this.onFiltersToggleClick}
          maxTotalResults={this.state.maxTotalResults}
          filters={this.state.filters}
          expandedFilters
        />
      </Fragment>
    );

  renderMap = () => (
    <section className="search-map">
      <NoSSR>
        <ContextBar
          className="search-context--map"
          city={this.props.city}
          pointOfInterest={this.state.pointOfInterest}
          onPlaceSelected={this.handlePlaceSelected}
          onPlaceUnselected={this.handlePlaceUnselected}
          isFiltersHidden={this.state.isFiltersHidden}
          countryCode={this.props.countryCode}
        />
        {isMobileScreen() && (
          <Button
            className={classNames('search-map__refresh-button', {
              'search-map__refresh-button--hidden': !this.state.newMapBoundaries
            })}
            color="filled"
            onClick={this.handleRefreshMapBoundaries}
            data-cy="searchHereButton"
          >
            <RefreshIcon />
            <span>{trans('search.search-here')}</span>
          </Button>
        )}
        <Map
          ref={map => {
            this[MAP_ID] = map;
          }}
          mapId={MAP_ID}
          currencyIsoCode={this.props.currency.isoCode}
          city={this.props.city}
          defaultZoom={this.props.clientConfig.defaultZoom[this.props.city.id]}
          markerList={this.state.markerList}
          pointOfInterest={this.state.pointOfInterest}
          adIdCardMouseEnter={this.state.adIdCardMouseEnter}
          adIdCardMouseLeave={this.state.adIdCardMouseLeave}
          adMarkerClicked={this.state.adMarkerClicked}
          adMarkerMouseEnter={this.state.adMarkerMouseEnter}
          adMarkerMouseLeave={this.state.adMarkerMouseLeave}
          onMarkerClicked={this.handleMarkerClicked}
          onMarkerMouseEnter={this.handleMarkerMouseEnter}
          onMarkerMouseLeave={this.handleMarkerMouseLeave}
          onMapDidMount={this.handleMapDidMount}
          onMapWillUnmount={this.handleMapWillUnmount}
          onIdle={this.handleMapNewBounds}
        />
      </NoSSR>
    </section>
  );

  renderMapConditionally = () => {
    const {
      clientConfig: { featureToggle }
    } = this.props;
    const { isMapActive, adMarkerClicked } = this.state;
    return !isMobileScreen()
      ? this.renderMap()
      : featureToggle.mapMobile && (
          <Fragment>
            {isMapActive && this.renderMap()}
            <div className="search-floating">
              <FloatingButton
                onClick={this.handleToggleActiveView}
                Icon={isMapActive ? ListIcon : MapIcon}
                data-cy={isMapActive ? 'listButton' : 'mapButton'}
                label={isMapActive ? trans('search.list') : trans('search.map')}
              />
              {isMapActive && adMarkerClicked && (
                <HomecardMap
                  data-cy="propertyCard"
                  id={adMarkerClicked.id}
                  filters={this.state.filters}
                />
              )}
            </div>
          </Fragment>
        );
  };

  renderPaginator = () => {
    const cityId = this.props.city.id;
    const { filters } = this.state;
    const nextIsDisabled = this.isNextButtonDisabled();
    const previousIsDisabled = this.isPrevButtonDisabled();

    const nextPageUrl = nextIsDisabled
      ? ''
      : AdUrlService.getSearchUrlFromFilters(cityId, {
          ...filters,
          page: this.cardPaginator.getNextPageNumber()
        });

    const previousPageUrl = previousIsDisabled
      ? ''
      : AdUrlService.getSearchUrlFromFilters(cityId, {
          ...filters,
          page: this.cardPaginator.getPreviousPageNumber()
        });

    return (
      this.cardPaginator.getTotalPages() > 1 && (
        <Paginator
          previousButtonDisabled={previousIsDisabled}
          nextButtonDisabled={nextIsDisabled}
          onPreviousButtonClick={this.handlePreviousButtonClick}
          onNextButtonClick={this.handleNextButtonClick}
          nextPageUrl={nextPageUrl}
          previousPageUrl={previousPageUrl}
        />
      )
    );
  };

  shouldRenderSeoContent = () =>
    this.state.filters.seoMode &&
    this.props.seoContent &&
    this.props.seoContent.body;

  handleScrollPositionObserver = isBottomOfHomecardListInView => {
    const body = document.body;
    const enableBodyScroll = () => body.classList.remove('lock-scroll');
    const disableBodyScroll = () => body.classList.add('lock-scroll');

    if (isBottomOfHomecardListInView) {
      enableBodyScroll();
      this.addSearchScrollListener();
    } else if (body.getBoundingClientRect().top >= 0) {
      disableBodyScroll();
    }
  };

  addSearchScrollListener = () => {
    const searchScroll = document.getElementById('search-scroll');
    searchScroll.addEventListener('scroll', this.searchScrollHandler);
  };

  searchScrollHandler = ({ target }) => {
    const homecardListScrolledUp = this.previousScrollTop > target.scrollTop;
    if (homecardListScrolledUp) this.hideSeoSection(target);
    this.previousScrollTop = target.scrollTop;
  };

  hideSeoSection = target => {
    window.scrollBy({
      top: -400,
      behavior: 'smooth'
    });
    setTimeout(() => document.body.classList.add('lock-scroll'), 400);
    target.removeEventListener('scroll', this.searchScrollHandler);
  };

  render() {
    const totalResults = this.cardPaginator.getOriginalLength();

    const {
      clientConfig: { featureToggle }
    } = this.props;

    return (
      <div id="wrapper-search">
        <div className="layout-search">
          {featureToggle.enableCityDiscounts && this.props.discounts && (
            <CityDiscountsToast
              city={this.props.city.id}
              updatedFilters={this.state.filters['type[]']}
              discounts={this.props.discounts}
            />
          )}
          <div className="search-sidebar">
            <div id="search-scroll" className="search-scroll">
              <NoSSR>
                <div className="search-filters">
                  <Filters
                    simpleFilters
                    filters={this.state.filters}
                    updateFiltersWith={this.updateFiltersState}
                    unitPrices={this.state.unitPrices}
                    currency={this.props.currency}
                    maxPriceInCity={this.props.maxPriceInCity}
                    minimumStaying={this.props.city.minimumStaying}
                    cityName={this.props.city.id}
                    areas={this.props.areas}
                  />
                </div>
                <FiltersHeader
                  results={this.state.numberOfMarkers}
                  totalResults={totalResults}
                  handleFiltersToggle={this.onFiltersToggleClick}
                  maxTotalResults={this.state.maxTotalResults}
                  expandedFilters={false}
                  orderBy={this.state.filters.sortBy}
                  handleSortChange={this.updateSortBy}
                  citySlug={this.props.city.id}
                  filters={this.state.filters}
                />
              </NoSSR>
              <div className="search-listing">
                <div className="search-listing__total-results">
                  <SearchBanner
                    cityId={this.props.city.id}
                    cancellationPolicies={this.state.cancellationPolicies}
                    moveIn={this.state.filters['move-in']}
                  />
                  <TotalResults
                    types={this.state.filters['type[]']}
                    citySlug={this.props.city.id}
                    totalResults={this.state.numberOfMarkers}
                    currentNeighborhood={this.state.filters['areaId[]']}
                  />
                </div>
                <HomeCardList
                  currentPage={this.cardPaginator.getCurrentPageNumber()}
                  cancellationPolicies={this.state.cancellationPolicies}
                  hasMap={this.state.isMapVisible}
                  initialHomecards={this.props.initialHomecards}
                  results={this.cardPaginator.getLength()}
                  totalResults={this.cardPaginator.getOriginalLength()}
                  markerList={this.state.markerList}
                  filters={this.state.filters}
                  onCardMouseEnter={this.handleCardMouseEnter}
                  onCardMouseLeave={this.handleCardMouseLeave}
                  adMarkerClicked={this.state.adMarkerClicked}
                  clearExtendedFilters={this.clearFilters}
                  calculateMarkersWithoutBounds={
                    this.calculateMarkersWithoutBounds
                  }
                  pointOfInterest={this.state.pointOfInterest}
                  cityId={this.props.city.id}
                  hasLazyLoad
                />
                {this.renderPaginator()}
                <InternalLinking />
                {this.shouldRenderSeoContent() && (
                  <Observer onChange={this.handleScrollPositionObserver} />
                )}
                <div
                  className="spinner-loading-map"
                  id="spinner-list-loader"
                  hidden
                >
                  <IconLoading />
                </div>
              </div>
            </div>
            <NoSSR>
              <ReactCSSTransitionGroup
                transitionName="search-filters"
                transitionEnterTimeout={500}
                transitionLeaveTimeout={200}
              >
                {this.renderFullFilters()}
              </ReactCSSTransitionGroup>
            </NoSSR>
          </div>
          {this.renderMapConditionally()}
        </div>
        {this.shouldRenderSeoContent() && (
          <SEOContent
            title={this.props.seoContent.title}
            subtitle={this.props.seoContent.subtitle}
            body={this.props.seoContent.body}
          />
        )}
      </div>
    );
  }
}

Search.propTypes = {
  sessionPoi: PropTypes.shape(poiShape),
  city: PropTypes.shape({
    id: PropTypes.string.isRequired,
    areaId: PropTypes.number.isRequired,
    limits: PropTypes.shape({
      ne: PropTypes.shape(mapPointShape).isRequired,
      sw: PropTypes.shape(mapPointShape).isRequired
    }).isRequired,
    minimumStaying: PropTypes.number.isRequired
  }).isRequired,
  countryCode: PropTypes.string,
  currency: PropTypes.shape({ isoCode: PropTypes.string }),
  maxPriceInCity: PropTypes.number.isRequired,
  initialFiltersFromQuery: PropTypes.shape({}),
  initialMarkerList: PropTypes.arrayOf(PropTypes.object),
  initialUnitPrices: PropTypes.arrayOf(PropTypes.number),
  clientConfig: PropTypes.shape({
    defaultZoom: PropTypes.shape({
      [PropTypes.string]: PropTypes.number
    }).isRequired,
    featureToggle: PropTypes.shape({
      enableCityDiscounts: PropTypes.bool,
      mapMobile: PropTypes.bool
    })
  }).isRequired,
  numberOfMarkers: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string,
  itemsPerPage: PropTypes.number.isRequired,
  initialHomecards: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      isInstantBooking: PropTypes.bool
    })
  }).isRequired,
  areas: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, id: PropTypes.number })
  ),
  discounts: PropTypes.arrayOf(
    PropTypes.shape({
      conditions: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string,
          value: PropTypes.string
        })
      ),
      discountValue: PropTypes.number
    })
  ),
  seoMode: PropTypes.bool,
  seoContent: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    body: PropTypes.string
  })
};

Search.defaultProps = {
  sessionPoi: {},
  initialFiltersFromQuery: {},
  sortingAlgorithm: '',
  initialMarkerList: [],
  initialUnitPrices: [],
  countryCode: '',
  currency: undefined,
  areas: [],
  discounts: [],
  seoMode: false,
  seoContent: null
};

export default withClientConfig()(Search);
